import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pin, PinOff, Trash2, Pencil, Save, X, StickyNote, Search, UserCircle2, PlayCircle, CheckCircle2, RotateCcw, Lock, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { id: null, title: "", body: "", pinned: false, assigned_to: "none", status: "open", visibility: "everyone", visible_to: [] };

const STATUS = {
  open:        { label: "Open",        tone: "slate",   chip: "bg-slate-100 text-slate-700 border-slate-200" },
  in_progress: { label: "In progress", tone: "sky",     chip: "bg-sky-50 text-sky-800 border-sky-200" },
  completed:   { label: "Completed",   tone: "emerald", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function Notes() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState(empty);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [n, u] = await Promise.all([api.get("/notes"), api.get("/users/lite")]);
      setRows(n.data || []);
      setUsers(u.data || []);
      // Auto-mark any unseen notes assigned to me as seen
      const mine = (n.data || []).filter((x) => x.assigned_to === me?.id && !x.seen_at);
      for (const x of mine) {
        try { await api.post(`/notes/${x.id}/seen`); } catch (_) { /* ignore */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter !== "all")   list = list.filter((n) => (n.status || "open") === statusFilter);
    if (assigneeFilter !== "all") {
      if (assigneeFilter === "unassigned") list = list.filter((n) => !n.assigned_to);
      else list = list.filter((n) => n.assigned_to === assigneeFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((n) =>
      [n.title, n.body, n.assigned_to_name, n.assigned_to_email, n.author_name, n.author_email]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [rows, search, statusFilter, assigneeFilter]);

  const startEdit = (n) => setDraft({
    id: n.id,
    title: n.title || "",
    body: n.body || "",
    pinned: !!n.pinned,
    assigned_to: n.assigned_to || "none",
    status: n.status || "open",
    visibility: (n.visible_to && n.visible_to.length > 0) ? "selected" : "everyone",
    visible_to: n.visible_to || [],
  });
  const startNew = () => setDraft(empty);
  const reset = () => setDraft(empty);

  const save = async () => {
    if (!draft.title.trim() && !draft.body.trim()) {
      toast.error("Give the note a title or some content first.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        body: draft.body,
        pinned: !!draft.pinned,
        assigned_to: draft.assigned_to === "none" ? null : draft.assigned_to,
        status: draft.status || "open",
        visible_to: draft.visibility === "selected" ? (draft.visible_to || []) : [],
      };
      if (draft.id) {
        await api.put(`/notes/${draft.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post("/notes", payload);
        toast.success(payload.assigned_to ? "Note saved and assigned" : "Note saved");
      }
      reset();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const togglePin = async (n) => {
    try {
      await api.put(`/notes/${n.id}`, {
        title: n.title || "", body: n.body || "", pinned: !n.pinned,
        assigned_to: n.assigned_to || null, status: n.status || "open",
        visible_to: n.visible_to || [],
      });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (n, status) => {
    try {
      await api.patch(`/notes/${n.id}/status`, { status });
      toast.success(`Marked ${STATUS[status]?.label || status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title || "this note"}"?`)) return;
    try {
      await api.delete(`/notes/${n.id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="notes-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Notes</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Notes</h1>
        <p className="text-sm text-slate-600 mt-1">A quiet place to jot anything — reminders, instructions, post-call follow-ups. Pin the important ones to the top.</p>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-5 space-y-3" data-testid="note-editor">
        <Input
          data-testid="note-title"
          value={draft.title}
          onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))}
          placeholder="Title (optional)"
          className="rounded-sm border-slate-300 font-medium"
        />
        <Textarea
          data-testid="note-body"
          value={draft.body}
          onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))}
          placeholder="Type your note here…"
          className="rounded-sm border-slate-300 min-h-[120px]"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="note-pin-toggle"
            variant="outline"
            size="sm"
            type="button"
            onClick={() => setDraft(d => ({ ...d, pinned: !d.pinned }))}
            className={`rounded-sm ${draft.pinned ? "border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-50" : "border-slate-300"}`}
          >
            {draft.pinned ? <Pin className="w-4 h-4 mr-1.5" /> : <PinOff className="w-4 h-4 mr-1.5" />}
            {draft.pinned ? "Pinned" : "Pin"}
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Assign to</Label>
            <Select value={draft.assigned_to} onValueChange={(v) => setDraft(d => ({ ...d, assigned_to: v }))}>
              <SelectTrigger data-testid="note-assignee" className="h-9 rounded-sm border-slate-300 min-w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No one (private note)</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Visible to</Label>
            <Select
              value={draft.visibility}
              onValueChange={(v) => setDraft(d => ({ ...d, visibility: v, visible_to: v === "everyone" ? [] : d.visible_to }))}
            >
              <SelectTrigger data-testid="note-visibility" className="h-9 rounded-sm border-slate-300 min-w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">Everyone</SelectItem>
                <SelectItem value="selected">Only selected users</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {draft.id && (
              <Button data-testid="note-cancel" variant="ghost" size="sm" onClick={reset} className="rounded-sm">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
            )}
            <Button
              data-testid="note-save"
              size="sm"
              disabled={saving}
              onClick={save}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
            >
              {draft.id ? <Save className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              {draft.id ? "Save changes" : "Add note"}
            </Button>
          </div>
        </div>
        {draft.visibility === "selected" && (
          <div className="rounded-sm border border-slate-200 bg-slate-50/60 p-3" data-testid="note-visible-picker">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-2">
              <Lock className="w-3 h-3" /> Who can see this note
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
              {users.map((u) => {
                const on = (draft.visible_to || []).includes(u.id);
                const isMe = u.id === me?.id;
                return (
                  <label
                    key={u.id}
                    data-testid={`note-visible-${u.id}`}
                    className={`inline-flex items-center gap-2 text-sm cursor-pointer select-none ${isMe ? "opacity-60" : ""}`}
                    title={isMe ? "You always see your own notes" : ""}
                  >
                    <input
                      type="checkbox"
                      checked={on || isMe}
                      disabled={isMe}
                      onChange={() => setDraft(d => ({
                        ...d,
                        visible_to: on
                          ? d.visible_to.filter((x) => x !== u.id)
                          : [...(d.visible_to || []), u.id],
                      }))}
                      className="h-3.5 w-3.5 rounded-sm accent-orange-500"
                    />
                    <span className="text-slate-800">{u.name || u.email}{isMe && <span className="text-[10px] text-slate-500 ml-1">(you)</span>}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              You always see your own notes. The owner and the assignee are also included automatically.
            </p>
          </div>
        )}
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="notes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, body or assignee name…"
            className="pl-9 rounded-sm border-slate-300"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="notes-status-filter" className="rounded-sm border-slate-300 w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger data-testid="notes-assignee-filter" className="rounded-sm border-slate-300 w-52"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 tabular-nums">{filtered.length} of {rows.length}</div>
        {draft.id !== null && (
          <Button variant="ghost" size="sm" onClick={startNew} className="rounded-sm" data-testid="notes-new">
            <Plus className="w-4 h-4 mr-1.5" /> New note
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card className="rounded-sm border border-dashed border-slate-300 shadow-none p-10 text-center">
          <StickyNote className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500">{rows.length === 0 ? "No notes yet — add your first one above." : "No notes match your search."}</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((n) => (
            <Card key={n.id} data-testid={`note-card-${n.id}`} className={`rounded-sm border shadow-none p-4 flex flex-col gap-2 ${n.pinned ? "border-orange-300 bg-orange-50/40" : "border-slate-200 bg-white"} ${n.status === "completed" ? "opacity-80" : ""}`}>
              <div className="flex items-start gap-2">
                <div className={`font-medium text-slate-900 flex-1 break-words ${n.status === "completed" ? "line-through decoration-emerald-500/60" : ""}`}>{n.title || "Untitled note"}</div>
                {n.pinned && <Pin className="w-3.5 h-3.5 text-orange-600 shrink-0 mt-1" />}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-sm border px-1.5 py-0.5 ${STATUS[n.status || "open"]?.chip || STATUS.open.chip}`} data-testid={`note-status-${n.id}`}>
                  {STATUS[n.status || "open"]?.label || "Open"}
                </span>
                {(n.visible_to && n.visible_to.length > 0) ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold rounded-sm border border-slate-300 bg-slate-100 text-slate-700 px-1.5 py-0.5"
                    title={`Visible to: ${(n.visible_to_names || []).join(", ")}`}
                    data-testid={`note-restricted-${n.id}`}
                  >
                    <Lock className="w-3 h-3" /> Restricted · {n.visible_to.length}
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium text-slate-500"
                    data-testid={`note-everyone-${n.id}`}
                    title="Visible to everyone"
                  >
                    <UsersIcon className="w-3 h-3" /> Everyone
                  </span>
                )}
              </div>
              {n.body && <div className={`text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed ${n.status === "completed" ? "line-through decoration-emerald-500/50" : ""}`}>{n.body}</div>}
              {(n.assigned_to_name || n.assigned_to_email) && (
                <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-sm px-2 py-1 self-start" data-testid={`note-assigned-${n.id}`}>
                  <UserCircle2 className="w-3.5 h-3.5 text-slate-500" />
                  Assigned to <span className="font-medium text-slate-800">{n.assigned_to === me?.id ? "you" : (n.assigned_to_name || n.assigned_to_email)}</span>
                </div>
              )}
              {n.status === "completed" && n.completed_by_name && (
                <div className="text-[11px] text-emerald-700">Completed by {n.completed_by_name}</div>
              )}

              {/* Status action buttons */}
              <div className="flex items-center gap-1.5 mt-1">
                {(n.status || "open") !== "in_progress" && (n.status || "open") !== "completed" && (
                  <button onClick={() => setStatus(n, "in_progress")} data-testid={`note-mark-progress-${n.id}`} className="text-[11px] rounded-sm border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 px-2 py-1 inline-flex items-center gap-1">
                    <PlayCircle className="w-3 h-3" /> Start
                  </button>
                )}
                {(n.status || "open") !== "completed" && (
                  <button onClick={() => setStatus(n, "completed")} data-testid={`note-mark-done-${n.id}`} className="text-[11px] rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-2 py-1 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Mark completed
                  </button>
                )}
                {n.status === "completed" && (
                  <button onClick={() => setStatus(n, "open")} data-testid={`note-mark-reopen-${n.id}`} className="text-[11px] rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 px-2 py-1 inline-flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Re-open
                  </button>
                )}
              </div>

              <div className="mt-auto pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                <span>Updated {(n.updated_at || "").slice(0, 10) || "—"}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePin(n)} title={n.pinned ? "Unpin" : "Pin to top"} data-testid={`note-pin-${n.id}`} className="p-1 rounded-sm hover:bg-slate-100 text-slate-600">
                    {n.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => startEdit(n)} title="Edit" data-testid={`note-edit-${n.id}`} className="p-1 rounded-sm hover:bg-slate-100 text-slate-600">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(n)} title="Delete" data-testid={`note-delete-${n.id}`} className="p-1 rounded-sm hover:bg-red-50 text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
